import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { navigate } from "gatsby";
import AuthService from "../services/AuthService";

interface User {
  username: string;
  // Add other user properties as needed
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (password: string, email: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // During SSR/SSG (gatsby build) there is no AuthProvider in the static render
    // tree and no browser storage to read, so the HTML must render logged-out.
    // Return a safe default instead of throwing - the throw previously broke
    // `gatsby build` for pages that call useAuth (e.g. /login). The real provider
    // hydrates on the client, where the misuse guard below still applies.
    if (typeof window === "undefined") {
      return {
        isAuthenticated: false,
        user: null,
        loading: true,
        login: async () => {},
        logout: () => {},
      };
    }
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const currentUser = AuthService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    }
    setLoading(false);
  }, []);

  const login = async (password: string, email: string) => {
    try {
      setLoading(true);
      const loggedInUser = await AuthService.login({ password, email });
      setUser(loggedInUser);
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    AuthService.logout();
    setUser(null);
    navigate("/login");
  };

  const value = {
    isAuthenticated: !!user,
    user,
    login,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
